import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { Bold, Italic, List, Mic, MicOff } from 'lucide-react';
import { cn } from '../lib/utils';

interface DiaryEditorProps {
  content?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
}

type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  onresult: ((event: any) => void) | null;
  onerror: ((event: any) => void) | null;
  onend: (() => void) | null;
};

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

export function DiaryEditor({
  content = '',
  onChange,
  placeholder = 'Write about today…',
}: DiaryEditorProps) {
  const [isRecording, setIsRecording] = React.useState(false);
  const [voiceSupported, setVoiceSupported] = React.useState(true);
  const [voiceStatus, setVoiceStatus] = React.useState('');
  const recognitionRef = React.useRef<SpeechRecognitionLike | null>(null);
  const finalTranscriptRef = React.useRef('');
  const interimTranscriptRef = React.useRef('');
  const stopRequestedRef = React.useRef(false);
  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'outline-none min-h-[80px] text-sm text-white leading-relaxed px-3 py-2.5 [&_p]:my-1 [&_ul]:my-1 [&_ul]:pl-5 [&_ul]:list-disc [&_ol]:my-1 [&_ol]:pl-5 [&_ol]:list-decimal [&_li]:my-0.5 [&_strong]:font-bold [&_em]:italic',
      },
    },
  });

  // Only sync from outside when content is cleared externally (delete/undo)
  // or when the editor is empty and saved content arrives (panel opens with existing diary).
  // We do NOT sync on every keystroke to avoid fighting the user while typing.
  const prevContentRef = React.useRef(content);
  React.useEffect(() => {
    if (!editor) return;
    const prev = prevContentRef.current;
    prevContentRef.current = content;
    const editorEmpty = editor.isEmpty;
    const incomingEmpty = !content || content === '<p></p>';
    if (content !== prev && (incomingEmpty || editorEmpty)) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  React.useEffect(() => {
    const speechWindow = window as Window & {
      SpeechRecognition?: SpeechRecognitionCtor;
      webkitSpeechRecognition?: SpeechRecognitionCtor;
    };
    const SpeechRecognitionImpl = speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition;
    if (!SpeechRecognitionImpl) {
      setVoiceSupported(false);
      return;
    }

    const recognition = new SpeechRecognitionImpl();
    recognition.lang = 'en-US';
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let interimTranscript = '';

      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        const transcript = result[0]?.transcript?.trim() ?? '';
        if (!transcript) continue;

        if (result.isFinal) {
          finalTranscriptRef.current = finalTranscriptRef.current
            ? `${finalTranscriptRef.current} ${transcript}`
            : transcript;
        } else {
          interimTranscript = transcript;
        }
      }

      interimTranscriptRef.current = interimTranscript;
      const liveText = [finalTranscriptRef.current, interimTranscriptRef.current].filter(Boolean).join(' ').trim();
      setVoiceStatus(liveText || 'Listening…');
    };

    recognition.onerror = () => {
      setIsRecording(false);
      setVoiceStatus('');
      finalTranscriptRef.current = '';
      interimTranscriptRef.current = '';
    };

    recognition.onend = () => {
      if (stopRequestedRef.current) {
        stopRequestedRef.current = false;
        setIsRecording(false);
        setVoiceStatus('');
        const transcript = finalTranscriptRef.current.trim();
        finalTranscriptRef.current = '';
        interimTranscriptRef.current = '';
        if (!transcript || !editor) return;

        editor.commands.focus('end');
        const currentHtml = editor.getHTML();
        const needsSpace = currentHtml && currentHtml !== '<p></p>' ? ' ' : '';
        editor.commands.insertContent(`${needsSpace}${transcript}`);
        return;
      }

      // Keep listening until the user explicitly taps Done.
      if (recognitionRef.current && isRecording) {
        try {
          recognitionRef.current.start();
        } catch {
          // Ignore restart errors; the current transcript remains intact.
        }
      }
    };

    recognitionRef.current = recognition;

    return () => {
      recognition.stop();
      recognitionRef.current = null;
    };
  }, [editor]);

  const toggleVoice = () => {
    if (!voiceSupported || !recognitionRef.current || !editor) return;

    if (isRecording) {
      stopRequestedRef.current = true;
      recognitionRef.current.stop();
      return;
    }

    finalTranscriptRef.current = '';
    interimTranscriptRef.current = '';
    stopRequestedRef.current = false;
    setVoiceStatus('Listening…');
    try {
      recognitionRef.current.start();
      setIsRecording(true);
      editor.commands.focus('end');
    } catch {
      setIsRecording(false);
      setVoiceStatus('');
    }
  };

  const stopVoice = () => {
    if (!recognitionRef.current || !isRecording) return;
    stopRequestedRef.current = true;
    recognitionRef.current.stop();
  };

  if (!editor) return null;

  return (
    <div className="rounded-xl overflow-hidden border border-white/30 focus-within:border-white/60 bg-white/20 transition-colors">
      <style>{`
        .koda-diary .tiptap p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: rgba(255,255,255,0.35);
          pointer-events: none;
          height: 0;
          font-style: italic;
          font-size: 0.875rem;
        }
        .koda-diary .tiptap p.is-editor-empty:first-child::after {
          content: 'or record and transcribe.';
          display: block;
          margin-top: 1.1rem;
          color: rgba(255,255,255,0.28);
          pointer-events: none;
          font-style: italic;
          font-size: 0.8125rem;
          line-height: 1.2;
        }
        .koda-diary .tiptap:focus { outline: none; }
      `}</style>
      {/* Formatting toolbar */}
      <div className="flex gap-0.5 px-2 pt-2 pb-1.5 border-b border-white/20">
          <button
            type="button"
            onMouseDown={e => { e.preventDefault(); editor.chain().focus().toggleBold().run(); }}
            className={cn(
              'p-1.5 rounded-lg transition-colors text-white/70 hover:text-white hover:bg-white/15',
              editor.isActive('bold') && 'bg-white/25 text-white'
            )}
            title="Bold"
          >
            <Bold size={14} strokeWidth={2.5} />
          </button>
          <button
            type="button"
            onMouseDown={e => { e.preventDefault(); editor.chain().focus().toggleItalic().run(); }}
            className={cn(
              'p-1.5 rounded-lg transition-colors text-white/70 hover:text-white hover:bg-white/15',
              editor.isActive('italic') && 'bg-white/25 text-white'
            )}
            title="Italic"
          >
            <Italic size={14} strokeWidth={2} />
          </button>
          <button
            type="button"
            onMouseDown={e => { e.preventDefault(); editor.chain().focus().toggleBulletList().run(); }}
            className={cn(
              'p-1.5 rounded-lg transition-colors text-white/70 hover:text-white hover:bg-white/15',
              editor.isActive('bulletList') && 'bg-white/25 text-white'
            )}
            title="Bullet list"
          >
            <List size={14} strokeWidth={2} />
          </button>
        </div>
      <div className="koda-diary">
        <EditorContent editor={editor} />
      </div>
      <div className="px-3 pb-3 pt-1.5 border-t border-white/10 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={toggleVoice}
            disabled={!voiceSupported || isRecording}
            className={cn(
              'inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors',
              isRecording
                ? 'bg-positive-green text-white cursor-default animate-pulse'
                : 'bg-white/15 text-white hover:bg-white/25',
              !voiceSupported && 'cursor-not-allowed opacity-50 hover:bg-white/15',
            )}
            title={voiceSupported ? 'Start voice transcription' : 'Voice transcription is not supported in this browser'}
          >
            <Mic size={14} />
            Record
          </button>
          {isRecording && (
            <button
              type="button"
              onClick={stopVoice}
              className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold bg-white text-primary-brown hover:bg-white/90 transition-colors"
              title="Done"
            >
              <MicOff size={14} />
              Done
            </button>
          )}
        </div>
        {isRecording && voiceStatus && (
          <p className="text-[11px] leading-tight text-white/60">{voiceStatus}</p>
        )}
      </div>
    </div>
  );
}
